import { sleep } from "@/lib/utils";

import { ApiError } from "../errors";
import type { Api, PlatformPermission, PlatformRole } from "../types";

let roles: PlatformRole[] = [
	{
		key: "platform-manager",
		name: "Platform manager",
		description: "Seller/shop curation and marketplace operations.",
		system: true,
		permissions: [
			"console.access",
			"page.products",
			"page.categories",
			"page.ideas",
			"page.orders",
			"page.carts",
			"page.shops",
			"page.users",
			"page.delivery",
			"page.geo",
			"page.notification_campaigns",
			"action.products.moderate",
			"action.categories.manage",
			"action.ideas.manage",
			"action.orders.manage",
			"action.shops.moderate",
			"action.shops.assign_manager",
			"action.delivery.manage",
			"action.geo.manage",
			"action.notifications.manage",
			"action.notifications.broadcast",
		],
		userCount: 0,
	},
	{
		key: "content-manager",
		name: "Content manager",
		description: "Catalog/content moderation and curation.",
		system: true,
		permissions: ["console.access", "page.products", "page.categories", "page.ideas", "page.inspiration_feed", "page.notification_campaigns", "action.products.moderate", "action.categories.manage", "action.ideas.manage", "action.inspiration_feed.manage"],
		userCount: 0,
	},
	{
		key: "support-manager",
		name: "Support manager",
		description: "Operational read access for support.",
		system: true,
		permissions: ["console.access", "page.orders", "page.carts", "page.shops", "page.users", "page.notification_campaigns", "action.notifications.manage", "page.support"],
		userCount: 0,
	},
];

const catalog: PlatformPermission[] = [
	{ key: "console.access", category: "system", label: "Admin console access" },
	...[
		"products",
		"categories",
		"ideas",
		"inspiration_feed",
		"orders",
		"carts",
		"shops",
		"users",
		"delivery",
		"geo",
		"platform_roles",
		"notification_campaigns",
		"support",
	].map((page) => ({ key: `page.${page}`, category: "pages", label: `Page: ${page}` })),
	...["products.moderate", "categories.manage", "ideas.manage", "inspiration_feed.manage", "orders.manage", "shops.moderate", "shops.assign_manager", "users.manage", "users.security", "delivery.manage", "geo.manage", "platform_roles.manage", "notifications.manage", "notifications.broadcast"].map((action) => ({ key: `action.${action}`, category: "actions", label: `Action: ${action}` })),
];

export const platformRoles: Api["platformRoles"] = {
	async catalog() {
		await sleep(100);
		return catalog;
	},
	async list() {
		await sleep(150);
		return roles;
	},
	async create(input) {
		await sleep(250);
		if (roles.some((role) => role.key === input.key)) throw new ApiError("Role already exists", 409, "CONFLICT");
		const role: PlatformRole = { ...input, system: false, userCount: 0 };
		roles = [...roles, role];
		return role;
	},
	async update(key, patch) {
		await sleep(250);
		const current = roles.find((role) => role.key === key);
		if (!current) throw new ApiError("Role not found", 404, "NOT_FOUND");
		const next = { ...current, ...patch, description: patch.description ?? current.description };
		roles = roles.map((role) => (role.key === key ? next : role));
		return next;
	},
	async remove(key) {
		await sleep(200);
		const role = roles.find((item) => item.key === key);
		if (!role) throw new ApiError("Role not found", 404, "NOT_FOUND");
		if (role.system) throw new ApiError("System role cannot be deleted", 400, "VALIDATION_ERROR");
		roles = roles.filter((item) => item.key !== key);
	},
};
